import { useState } from "react";
import { Outlet, useNavigate, NavLink } from "react-router-dom";
import { LayoutDashboard, Users, Wrench, ReceiptText, Plug, Settings2, Sun, Moon, LogOut, Menu, UserPlus, MessageSquare, PhoneOutgoing, RefreshCw, UsersRound, UserCircle, BriefcaseBusiness } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { LOGO, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationBell from "@/components/NotificationBell";
import ChatWidget from "@/components/ChatWidget";
import CustomerFormDialog from "@/components/CustomerFormDialog";

const ADMIN_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/clients", label: "Customers", icon: Users },
  { to: "/renewals", label: "Renewals", icon: RefreshCw },
  { to: "/tech-cases", label: "My Cases", icon: Wrench },
  { to: "/invoices", label: "Invoices", icon: ReceiptText },
  { to: "/outbound-calls", label: "Outbound Calls", icon: PhoneOutgoing },
  { to: "/users", label: "Team", icon: UsersRound },
  { to: "/hr", label: "HR Admin", icon: BriefcaseBusiness },
  { to: "/me", label: "My Space", icon: UserCircle },
  { to: "/settings", label: "Settings", icon: Settings2 },
  { to: "/integrations", label: "Integrations", icon: Plug },
];
const AGENT_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/my-calls", label: "My Calls", icon: PhoneOutgoing },
  { to: "/tech-cases", label: "My Cases", icon: Wrench },
  { to: "/me", label: "My Space", icon: UserCircle },
];
const HR_EXTRA = { to: "/hr", label: "HR Admin", icon: BriefcaseBusiness };

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const isHr = (user?.role || "").toLowerCase() === "hr";
  const NAV = isAdmin ? ADMIN_NAV : (isHr ? [AGENT_NAV[0], AGENT_NAV[2], HR_EXTRA, AGENT_NAV[3]] : AGENT_NAV);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className={`fixed z-40 md:static inset-y-0 left-0 w-64 bg-[hsl(var(--sidebar))] border-r border-border flex flex-col transition-transform ${mobileNav ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="h-16 flex items-center px-4 border-b border-border">
          <span className="flex bg-white rounded-sm w-full h-12 overflow-hidden items-center justify-center"><img src={LOGO} alt="Zeefa Technologies" className="w-full h-full object-cover object-center" data-testid="sidebar-logo" /></span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setMobileNav(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-150 ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"}`}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.photo_path
              ? <img src={fileUrl(user.photo_path)} alt={user?.name} className="h-9 w-9 rounded-sm object-cover border border-border" data-testid="sidebar-avatar" />
              : <div className="h-9 w-9 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-heading font-semibold text-sm">{(user?.name || "?").slice(0, 1).toUpperCase()}</div>}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate capitalize">{user?.role}</p>
            </div>
            <button onClick={handleLogout} data-testid="logout-btn" className="text-muted-foreground hover:text-primary transition-colors"><LogOut size={18} /></button>
          </div>
        </div>
      </aside>

      {mobileNav && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setMobileNav(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 flex items-center gap-3 px-4 md:px-6 border-b border-border bg-background/80 backdrop-blur-xl">
          <button className="md:hidden text-muted-foreground" onClick={() => setMobileNav(true)} data-testid="mobile-nav-toggle"><Menu /></button>
          <GlobalSearch />
          <div className="flex items-center gap-2 ml-auto">
            <Button onClick={() => setFormOpen(true)} data-testid="new-customer-btn" className="rounded-sm gap-2"><UserPlus size={16} /> <span className="hidden sm:inline">New Customer</span></Button>
            <NotificationBell />
            <Button variant="outline" size="icon" onClick={toggle} data-testid="theme-toggle" className="rounded-sm">{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-[1400px] w-full"><Outlet /></main>
      </div>

      <CustomerFormDialog open={formOpen} onOpenChange={setFormOpen} onSaved={() => window.dispatchEvent(new Event("zeefa:refresh"))} />
      <ChatWidget />
    </div>
  );
}
